import posthog from "posthog-js";
import { POSTHOG_EVENTS } from "../constants/PostHogEvents";
import { getSelectedOrganization } from "../helpers/localStorage";
import { getCurrentEnv } from "../config/env.ts";

interface UserData {
	uid?: string;
	email?: string;
}
interface CaptureOptions {
	send_instantly?: boolean;
	timestamp?: Date | string | number;
}

class PostHogService {
	private buildCaptureOptions(options?: CaptureOptions): any {
		const captureOptions: any = { send_instantly: options?.send_instantly };
		if (options?.timestamp) {
			captureOptions.timestamp =
				options.timestamp instanceof Date ? options.timestamp : new Date(options.timestamp);
		}
		return captureOptions;
	}

	private getUserAndOrgData(user?: UserData | null): Record<string, any> {
		const selectedOrg = getSelectedOrganization();
		const userData: Record<string, any> = user
			? {
					user_id: user.uid ?? null,
					email: user.email ?? null,
			  }
			: {};

		const orgData: Record<string, any> = selectedOrg
			? {
					org_id: selectedOrg?.organization?.uid ?? null,
					org_name: selectedOrg?.organization?.name ?? null,
					org_role: selectedOrg?.role ?? null,
					environment: getCurrentEnv(),
			  }
			: {};

		return { ...userData, ...orgData };
	}

	trackEvent(
		eventName: POSTHOG_EVENTS | string,
		properties?: Record<string, any>,
		user?: UserData | null,
		options?: CaptureOptions,
	) {
		const contextData = this.getUserAndOrgData(user);
		const enrichedProperties = { ...properties, ...contextData };

		if (properties?.email) {
			posthog.identify(properties.email);
		}

		posthog.capture(eventName, enrichedProperties, this.buildCaptureOptions(options));
	}

	trackPageView(
		pageName: string,
		properties?: Record<string, any>,
		user?: UserData | null,
		options?: CaptureOptions,
	) {
		const contextData = this.getUserAndOrgData(user);
		const enrichedProperties = {
			page_name: pageName,
			$current_url: window.location?.href ?? null,
			...properties,
			...contextData,
		};

		posthog.capture("$pageview", enrichedProperties, this.buildCaptureOptions(options));
	}

	trackFeatureUsage(
		feature: string,
		properties?: Record<string, any>,
		user?: UserData | null,
		options?: CaptureOptions,
	) {
		const contextData = this.getUserAndOrgData(user);
		const enrichedProperties = {
			feature,
			...properties,
			...contextData,
		};

		posthog.capture(POSTHOG_EVENTS.FEATURE_USED, enrichedProperties, this.buildCaptureOptions(options));
	}

	trackError(
		error: string,
		context?: string,
		properties?: Record<string, any>,
		user?: UserData | null,
		options?: CaptureOptions,
	) {
		const contextData = this.getUserAndOrgData(user);
		const enrichedProperties = {
			error,
			context: context ?? null,
			...properties,
			...contextData,
		};

		posthog.capture(POSTHOG_EVENTS.ERROR_OCCURRED, enrichedProperties, this.buildCaptureOptions(options));
	}
}

export const postHogService = new PostHogService();
export default postHogService;
